import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  getOpenShelvesQueryKey,
  getShelfDetectionsQueryKey,
  getSubmissionQueryKey,
  type ShelfStatusBodyStatus,
  usePatchShelfStatus,
  type WorkspaceDetectionsResponseOutput,
} from '@/api/generated';

interface UseShelfStatusArgs {
  submissionId: string;
  shelveId: string;
}

// Persists a shelf status change and keeps the workspace and shelves-list
// query caches in sync. Returns whether the patch succeeded so callers can
// skip navigation on failure.
export const useShelfStatus = ({
  submissionId,
  shelveId,
}: UseShelfStatusArgs) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutateAsync: patchShelfStatus, isPending } = usePatchShelfStatus();

  const setStatus = useCallback(
    async (next: ShelfStatusBodyStatus) => {
      try {
        await patchShelfStatus({
          submissionId,
          shelveId,
          data: { status: next },
        });
      } catch {
        toast.error(t('shelfDetail.controls.statusUpdateFailed'));
        return false;
      }

      queryClient.setQueryData(
        getShelfDetectionsQueryKey(submissionId, shelveId),
        (old: WorkspaceDetectionsResponseOutput | undefined) =>
          old ? { ...old, status: next } : old
      );
      queryClient
        .invalidateQueries({ queryKey: getSubmissionQueryKey(submissionId) })
        .catch(() => undefined);
      queryClient
        .invalidateQueries({ queryKey: getOpenShelvesQueryKey() })
        .catch(() => undefined);
      return true;
    },
    [patchShelfStatus, queryClient, shelveId, submissionId, t]
  );

  return { setStatus, isPending };
};
